import { createEffect, createMemo, createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { RepoOverrideContext } from './AgentRepoOverrideRow';
import { AgentRouteAdvancedFields } from './AgentRouteAdvancedFields';
import { Field } from './Field';
import { ModelPicker } from './ModelPicker';
import { ModelSaveNotice } from './ModelSaveNotice';
import { Select, type SelectOption } from './Select';
import { Button } from './ui/Button';

// "Add a repo override" (#5211 AC3 item 1): the list above only lists repos that
// ALREADY have an override — it cannot CREATE a first pin. This slot offers every
// ENABLED repo the account holds (`repoNames`, already `useRepoList` — no second
// discovery call) that is NOT already listed, through the SAME `ModelPicker`
// (`choose(repoId, purpose, undefined, change)` sends `{absent: true}`, same as any
// fresh pin). It is its OWN row, not folded into the list — the refusal/busy state it
// drives is keyed on the SAME (repoId, purpose) a picked repo would use, so once the
// pin lands (or a 409 reveals someone else's row for that repo), the repo joins the
// list above and the slot resets (never two controls answering for the same repo at
// once).
//
// COMPACT (the Default card, #5626): the slot sits behind an "Add override" button
// rather than standing open under the list.

export type AgentRepoOverrideAddProps = {
  readonly ctx: RepoOverrideContext;
  /** Every enabled repo NOT already listed, `[id, full_name]`, sorted by name. */
  readonly candidates: readonly (readonly [string, string])[];
  readonly compact: boolean;
};

export function AgentRepoOverrideAdd(props: AgentRepoOverrideAddProps): JSX.Element {
  const [addingRepoId, setAddingRepoId] = createSignal('');
  const [revealed, setRevealed] = createSignal(false);
  /** `candidates` as `Select` options, the placeholder leading — itself a real,
   *  pickable option (the native select's first `<option value="">` was always
   *  clickable too, never hidden once the list was open). */
  const addRepoOptions = createMemo((): readonly SelectOption<string>[] => [
    { value: '', label: t('agents.drawer.repo.add_placeholder') },
    ...props.candidates.map(([id, name]) => ({ value: id, label: name })),
  ]);
  // The repo the slot is mid-pick for just joined the list — either this write landed,
  // or a 409 re-read revealed another writer's row for it. Either way that row now
  // answers for (repoId, purpose); reset the slot so its `ModelPicker`/notice don't
  // keep rendering a second answer for the SAME key the list row just took over.
  createEffect(() => {
    if (addingRepoId() !== '' && !props.candidates.some(([id]) => id === addingRepoId())) {
      setAddingRepoId('');
    }
  });
  const purpose = () => props.ctx.purpose;
  const choose = (change: Parameters<typeof props.ctx.saves.choose>[3]): void =>
    void props.ctx.saves.choose(addingRepoId(), purpose(), undefined, change);

  const slot = (): JSX.Element => (
    <div class="agent-drawer__repo-add" id={`agent-drawer-repo-add-${purpose()}-slot`}>
      <Field label={t('agents.drawer.repo.add_label')} id={`agent-drawer-repo-add-${purpose()}`}>
        {(a11y) => (
          <Select
            a11y={a11y}
            label={t('agents.drawer.repo.add_label')}
            value={addingRepoId()}
            options={addRepoOptions()}
            onChange={setAddingRepoId}
            block
          />
        )}
      </Field>
      <Show when={addingRepoId() !== ''}>
        <ModelPicker
          agentName={props.ctx.agentName}
          purpose={purpose()}
          route={undefined}
          pinValue={null}
          keys={props.ctx.keys}
          canEdit={props.ctx.canEdit}
          busy={props.ctx.saves.isBusy(addingRepoId(), purpose())}
          onChoose={choose}
        />
        <AgentRouteAdvancedFields
          idPrefix={`agent-drawer-repo-add-${purpose()}-fields`}
          route={undefined}
          keys={props.ctx.keys}
          canEdit={props.ctx.canEdit}
          busy={props.ctx.saves.isBusy(addingRepoId(), purpose())}
          onChange={choose}
          token={props.ctx.token}
          onKeyRenamed={props.ctx.onKeyRenamed}
        />
        <ModelSaveNotice
          pending={props.ctx.saves.refusal(addingRepoId(), purpose())}
          busy={props.ctx.saves.isBusy(addingRepoId(), purpose())}
          onApplyAnyway={() => void props.ctx.saves.applyAnyway(addingRepoId(), purpose())}
          onApplyReset={() => void props.ctx.saves.applyWithoutEffort(addingRepoId(), purpose())}
          onDismiss={() => props.ctx.saves.dismiss(addingRepoId(), purpose())}
        />
      </Show>
    </div>
  );

  return (
    <Show when={props.compact} fallback={slot()}>
      <Button
        variant="outline"
        size="sm"
        aria-expanded={revealed()}
        aria-controls={`agent-drawer-repo-add-${purpose()}-slot`}
        onClick={() => setRevealed(!revealed())}
      >
        {t('agents.drawer.repo.add_button')}
      </Button>
      <Show when={revealed()}>{slot()}</Show>
    </Show>
  );
}
